import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { ChevronDown, Flame, FlaskConical, Loader2, Play, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

import { GET_KID_HOME } from "@/graphql/queries/kid";
import { GET_LAB_SESSIONS } from "@/graphql/queries/situations";
import { CREATE_SESSION, GENERATE_EXERCISES } from "@/graphql/mutations/sessions";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { SubjectIcon } from "@/components/subject-icon";
import { useAuth } from "@/lib/auth/AuthContext";
import { FEATURE_DECISION_LAB, useFeature } from "@/lib/features";
import { formatRelativeDate } from "@/lib/relative-date";
import {
  daysLabel,
  exerciseCountKey,
  exercisesLabel,
  pointsLabel,
  rememberedExerciseCount,
  EXERCISE_COUNT_CHOICES,
  EXERCISE_COUNT_FALLBACK,
} from "@/lib/students";

/**
 * What a learner sees when the device is theirs: finish what is open, or pick a
 * subject and start. Nothing about other learners, nothing to configure.
 *
 * Tapping a subject starts immediately — the length is decided by the control
 * above the grid, which defaults to whatever a grown-up set for this learner (or
 * failing that, however long their last session was). A child
 * who just wants to practise never has to answer a question first; one who wants
 * a short round can say so in one tap.
 */
export default function KidHomePage() {
  const { student: me } = useAuth();
  const hasDecisionLab = useFeature(FEATURE_DECISION_LAB);
  const navigate = useNavigate();
  const [starting, setStarting] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  // Only while the section is on: a flag that is off should cost no request.
  const { data: labData } = useQuery(GET_LAB_SESSIONS, {
    variables: { studentId: me?.id },
    skip: !me?.id || !hasDecisionLab,
    fetchPolicy: "cache-and-network",
  });
  const labSession = ((labData as any)?.decisionLab?.sessions ?? []).find(
    (s: { done: boolean }) => !s.done
  );

  const { data, loading } = useQuery(GET_KID_HOME, {
    variables: { id: me?.id },
    skip: !me?.id,
    fetchPolicy: "cache-and-network",
  });

  const [createSession] = useMutation(CREATE_SESSION);
  const [generateExercises] = useMutation(GENERATE_EXERCISES);

  const student = (data as any)?.student;
  const subjects = (data as any)?.learnerSubjects ?? [];
  // Only work that is actually ready to be solved — a half-built session would
  // just be a dead end. Whether a grown-up ticked "savarankiškas" does not come
  // into it: a session shown in the kid shell is one the child solves here, and
  // opening it says so (see SubmitStudentAnswers).
  const unfinished = ((data as any)?.sessions ?? []).filter(
    (s: any) => s.status === "GENERATED" && s.exerciseCount > 0
  );

  // A subject with no exercises on disk cannot start anything, so it stays out
  // of the way until content exists — but it is never removed outright, because
  // "where did biology go" is a worse surprise than an empty subject.
  const ready = subjects.filter((s: any) => s.recommended);
  const rest = subjects.filter((s: any) => !s.recommended);
  const visible = showAll ? [...ready, ...rest] : ready;

  // Only a deliberate choice is held in state. Anything else defers to the
  // server's suggestion — which arrives a moment after first paint, so baking a
  // default into state here would quietly discard it.
  useEffect(() => {
    const remembered = rememberedExerciseCount(me?.id);
    if (remembered) setCount(remembered);
  }, [me?.id]);

  const chooseCount = (value: number) => {
    setCount(value);
    if (me?.id) localStorage.setItem(exerciseCountKey(me.id), String(value));
  };

  const effectiveCount = count ?? student?.suggestedExerciseCount ?? EXERCISE_COUNT_FALLBACK;

  /**
   * How many questions this subject will actually serve.
   *
   * The length control is one control for the whole grid, and a grown-up may
   * have capped one subject shorter than the child's pick. The server holds them
   * to it either way — this is so the number on screen is the number they get,
   * rather than a promise the session then quietly breaks.
   *
   * A cap is a ceiling and never a floor: choosing a shorter round than allowed
   * is exactly what the control is for.
   */
  const countFor = (subjectKey: string) => {
    const limits = (student?.subjectSessionLengths ?? []).find(
      (row: { subjectKey: string }) => row.subjectKey === subjectKey
    );
    if (count == null) return limits?.effectiveCount ?? effectiveCount;

    return limits?.count ? Math.min(count, limits.count) : count;
  };

  const start = async (subjectKey: string) => {
    if (!me?.id || starting) return;
    setStarting(subjectKey);

    try {
      const { data: created } = await createSession({
        variables: { studentId: me.id, subjectKey },
      });

      const session = (created as any)?.createSession?.session;
      const createErrors = (created as any)?.createSession?.errors ?? [];
      if (!session) {
        toast.error(createErrors[0] ?? "Nepavyko pradėti");
        return;
      }

      const { data: generated } = await generateExercises({
        variables: { sessionId: session.id, targetExerciseCount: countFor(subjectKey) },
      });

      const genErrors = (generated as any)?.generateExercises?.errors ?? [];
      if (genErrors.length > 0) {
        toast.error(genErrors[0]);
        return;
      }

      navigate(`/sessions/${session.id}/solve`);
    } catch {
      toast.error("Nepavyko pradėti");
    } finally {
      setStarting(null);
    }
  };

  if (loading && !student) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const level = student?.level;
  const streak = student?.currentStreak ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Labas, {student?.name?.split(" ")[0] ?? ""}!
        </h1>
        <p className="text-muted-foreground">Ką norėtum spręsti šiandien?</p>
      </div>

      {/* What they have earned, before what there is to do — on a phone this is
          the first screenful, and it is the half of the page that is about them.
          The level was already being fetched and never drawn; the bar is what
          turns a number nobody asked for into a distance worth closing. */}
      <section className="rounded-2xl border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {level && (
            <>
              <span className="text-2xl leading-none" aria-hidden="true">
                {level.icon}
              </span>
              <span className="font-semibold">{level.name}</span>
            </>
          )}
          <span className="ml-auto flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-sm">
              <span aria-hidden="true">💎</span>
              <span className="font-semibold tabular-nums">
                {(student?.totalPoints ?? 0).toLocaleString()}
              </span>
            </span>
            {streak > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-sm"
                title={`${daysLabel(streak)} iš eilės`}
              >
                <Flame className="h-3.5 w-3.5 text-brand-accent" aria-hidden="true" />
                <span className="font-semibold tabular-nums">{streak}</span>
              </span>
            )}
          </span>
        </div>

        {level?.nextLevel && (
          <div className="mt-3 max-w-md space-y-1.5">
            <Progress value={level.progressToNextLevel ?? 0} className="h-2" />
            <p className="text-xs text-muted-foreground">
              Dar {pointsLabel(level.pointsToNextLevel ?? 0)} iki „{level.nextLevel.name}“
            </p>
          </div>
        )}

        {streak > 0 && (
          <p className="mt-3 text-sm text-muted-foreground">
            🔥 Sprendi {daysLabel(streak)} iš eilės — nenutrauk!
          </p>
        )}
      </section>

      {unfinished.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Tęsk
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {unfinished.map((session: any) => (
              <button
                key={session.id}
                onClick={() => navigate(`/sessions/${session.id}/solve`)}
                // The started session outranks a new one, so it is the only card
                // on the page carrying the primary colour.
                className="flex items-center gap-4 rounded-2xl border border-primary/40 bg-primary/5 p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99]"
              >
                <SubjectIcon
                  icon={session.subject?.icon}
                  className="h-9 w-9 shrink-0 text-primary"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">
                    {session.subject?.name ?? session.subjectKey}
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    {exercisesLabel(session.exerciseCount)}
                  </span>
                </span>
                <Play className="h-6 w-6 shrink-0 text-primary" />
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Pradėk naują
          </h2>

          {/* Quiet by default: it already says what will happen, so most of the
              time there is nothing to do here. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full">
                <SlidersHorizontal className="mr-2 h-3.5 w-3.5" />
                {exercisesLabel(effectiveCount)}
                <ChevronDown className="ml-1 h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {EXERCISE_COUNT_CHOICES.map((choice) => (
                <DropdownMenuItem key={choice} onClick={() => chooseCount(choice)}>
                  <span className={choice === effectiveCount ? "font-semibold" : undefined}>
                    {exercisesLabel(choice)}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Two across on a phone rather than one: a full-width tile per subject
            turned four choices into a scroll, and the tile was mostly padding. */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {visible.map((subject: any) => (
            <button
              key={subject.id}
              onClick={() => start(subject.id)}
              disabled={starting !== null || !subject.available}
              title={subject.available ? undefined : "Kol kas užduočių nėra"}
              className="flex min-h-[7.5rem] flex-col items-center justify-center gap-2 rounded-2xl border bg-card p-4 text-center shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg active:scale-[0.97] disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sm sm:min-h-[9rem] sm:gap-3 sm:p-6"
            >
              <SubjectIcon icon={subject.icon} className="h-10 w-10 text-primary sm:h-12 sm:w-12" />
              <span className="text-base font-semibold sm:text-lg">{subject.name}</span>
              {starting === subject.id ? (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Ruošiu…
                </span>
              ) : !subject.available ? (
                <span className="text-xs text-muted-foreground sm:text-sm">Kol kas tuščia</span>
              ) : countFor(subject.id) !== effectiveCount ? (
                // A grown-up capped this subject below what the control says, so
                // the button above is not the truth here. Which it will be is
                // worth more than when they last practised.
                <span className="text-xs font-medium text-muted-foreground">
                  {exercisesLabel(countFor(subject.id))}
                </span>
              ) : (
                subject.lastSolvedAt && (
                  // Also already fetched and unused. It answers "which of these
                  // have I been neglecting" without ranking them for the child.
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeDate(subject.lastSolvedAt)}
                  </span>
                )
              )}
            </button>
          ))}
        </div>

        {rest.length > 0 && (
          <div className="pt-1">
            <button
              onClick={() => setShowAll((v) => !v)}
              className="-mx-2 rounded-lg px-2 py-2 text-sm text-muted-foreground underline-offset-4 hover:underline"
            >
              {showAll ? "Rodyti mažiau" : `Rodyti visus dalykus (${rest.length})`}
            </button>
          </div>
        )}
      </section>

      {/* Below the subjects and outside their grid, deliberately: this is not a
          seventh subject. Nothing here is timed, scored or streaked, so putting
          it among the tiles that start a graded session would promise the wrong
          thing. The kid shell has no navigation of its own — see kid-layout —
          so the home page is where a section becomes reachable at all. */}
      {hasDecisionLab && (
        <section>
          {/* A waiting set is what the card says when there is one — a grown-up
              prepared it, and „go and look" is a worse answer than „it is ready".
              Solid rather than dashed then, because it is now something to do
              rather than somewhere to browse. The count is sheets written in,
              never a score. */}
          <Link
            to={labSession ? `/lab/session/${labSession.id}` : "/lab"}
            className={`flex items-center gap-4 rounded-2xl border bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
              labSession ? "border-primary/50 bg-primary/5" : "border-dashed"
            }`}
          >
            <FlaskConical className="h-9 w-9 shrink-0 text-primary" />
            <span className="min-w-0 flex-1">
              <span className="block font-semibold">
                {labSession
                  ? labSession.started
                    ? "Tęsk situacijų sesiją"
                    : "Tau paruošta situacijų sesija"
                  : "Sprendimų laboratorija"}
              </span>
              <span className="block text-sm text-muted-foreground">
                {labSession
                  ? `${labSession.doneCount} iš ${labSession.size} atsakyta`
                  : "Situacijos, kuriose nėra vieno teisingo atsakymo"}
              </span>
            </span>
          </Link>
        </section>
      )}
    </div>
  );
}
